import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, Users, Eye, TrendingUp } from "lucide-react";

interface KPIData {
  activeJobs: number;
  totalApplications: number;
  candidatesViewed: number;
  applicationsThisMonth: number;
}

export default function EmployerKPIs() {
  const [kpis, setKpis] = useState<KPIData>({
    activeJobs: 0,
    totalApplications: 0,
    candidatesViewed: 0,
    applicationsThisMonth: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Calculate start of current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      // Fetch employer's job posting IDs first
      const { data: employerJobs } = await supabase
        .from("job_postings")
        .select("id")
        .eq("employer_id", user.id);

      const jobIds = employerJobs?.map(j => j.id) || [];

      // Fetch all KPIs in parallel
      const [jobsRes, applicationsRes, viewsRes, monthlyAppsRes] = await Promise.all([
        // Active job postings
        supabase
          .from("job_postings")
          .select("id", { count: "exact", head: true })
          .eq("employer_id", user.id)
          .eq("status", "active"),

        // Total applications received for employer's jobs
        jobIds.length > 0
          ? supabase
              .from("job_applications")
              .select("id", { count: "exact", head: true })
              .in("job_posting_id", jobIds)
          : Promise.resolve({ count: 0 }),

        // Candidates viewed (profile views)
        supabase
          .from("profile_views")
          .select("id", { count: "exact", head: true })
          .eq("viewer_id", user.id)
          .gte("viewed_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),

        // Applications this month for employer's jobs
        jobIds.length > 0
          ? supabase
              .from("job_applications")
              .select("id", { count: "exact", head: true })
              .in("job_posting_id", jobIds)
              .gte("applied_at", startOfMonth)
          : Promise.resolve({ count: 0 })
      ]);

      setKpis({
        activeJobs: jobsRes.count || 0,
        totalApplications: applicationsRes.count || 0,
        candidatesViewed: viewsRes.count || 0,
        applicationsThisMonth: monthlyAppsRes.count || 0,
      });
    } catch (error) {
      console.error("Error fetching employer KPIs:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
          <Briefcase className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.activeJobs}</div>
          <p className="text-xs text-muted-foreground">
            Currently hiring
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.totalApplications}</div>
          <p className="text-xs text-muted-foreground">
            All time
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">This Month</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.applicationsThisMonth}</div>
          <p className="text-xs text-muted-foreground">
            New applications
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Candidates Viewed</CardTitle>
          <Eye className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{kpis.candidatesViewed}</div>
          <p className="text-xs text-muted-foreground">
            Last 30 days
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
